import { createContext, useContext, useEffect, useState, useCallback, useRef, type ReactNode } from 'react'
import { supabase } from '../lib/supabase'
import { setSentryUser, clearSentryUser } from '../lib/sentry'
import { clearCrossUserLeakResidue } from '../lib/userScopedStorage'
import { useRefreshOnFocus } from '../hooks/useRefreshOnFocus'
import type { User } from '@supabase/supabase-js'

interface Profile {
  id: string
  email: string
  first_name: string | null
  credits: number
  role: string
  subscription_tier: string | null
  subscription_status: string | null
  total_analyses: number
  onboarding_completed: boolean
}

interface AuthContextType {
  user: User | null
  profile: Profile | null
  loading: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  signIn: (email: string, password: string) => Promise<{ error: any }>
  signOut: () => Promise<void>
  refreshProfile: () => Promise<void>
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  profile: null,
  loading: true,
  signIn: async () => ({ error: null }),
  signOut: async () => {},
  refreshProfile: async () => {},
})

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(true)

  // 2026-04-23 — Break the SIGNED_IN-cascade at tab-return.
  //
  // Supabase JS's built-in visibility handler runs `_recoverAndRefresh` when
  // the tab becomes visible again. When it succeeds it emits a synthetic
  // `SIGNED_IN` event with the SAME access_token the client already had.
  // Without a guard, that triggered a full fetchProfile() → which issued a
  // `supabase.from('profiles').select(…)` → which internally called
  // getSession() → which checked refresh → which re-emitted SIGNED_IN → loop.
  // The cascade saturated the GoTrueClient auth mutex and all subsequent
  // queries (PostgREST selects on every BN sub-route) hung indefinitely
  // until their own 10 s Promise.race timeout tripped, surfacing as
  // "Impossible de charger les enregistrements" across BN, Dashboard,
  // Results, CRM.
  //
  // We compare session.access_token to the previous one. Identical → early
  // return, no fetchProfile, no cascade. Genuine sign-ins (different token)
  // and SIGNED_OUT/TOKEN_REFRESHED events pass through normally.
  const prevAccessTokenRef = useRef<string | null>(null)

  const fetchProfile = useCallback(async (userId: string) => {
    let { data } = await supabase
      .from('profiles')
      .select('id, email, first_name, credits, role, subscription_tier, subscription_status, total_analyses, onboarding_completed')
      .eq('id', userId)
      .maybeSingle()
    // Self-healing: if the profile row is missing (auth.users INSERT trigger
    // dropped on Dev 2026-04-20, or brand-new signup that just confirmed
    // email), bootstrap it via SECURITY DEFINER RPC and re-fetch. Idempotent
    // on the server side — safe to retry.
    if (!data) {
      // Cast through `never` — `bootstrap_user_profile` was added in migration
      // 20260420_bootstrap_user_profile_rpc (post-generated types regen).
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error: bootstrapErr } = await (supabase.rpc as any)('bootstrap_user_profile')
      if (!bootstrapErr) {
        const retry = await supabase
          .from('profiles')
          .select('id, email, first_name, credits, role, subscription_tier, subscription_status, total_analyses, onboarding_completed')
          .eq('id', userId)
          .maybeSingle()
        data = retry.data
      }
    }
    if (data) {
      const next = data as Profile
      // Stabilize reference when nothing material changed. Supabase Realtime
      // + TOKEN_REFRESHED events otherwise create a new profile object on
      // every heartbeat, cascading re-renders through every consumer
      // (Navbar credits, Dashboard stats, BetterNotesLayout…). That cascade
      // was the Result→Dashboard "info loops" symptom.
      setProfile((prev) => {
        if (
          prev &&
          prev.id === next.id &&
          prev.email === next.email &&
          prev.first_name === next.first_name &&
          prev.credits === next.credits &&
          prev.role === next.role &&
          prev.subscription_tier === next.subscription_tier &&
          prev.subscription_status === next.subscription_status &&
          prev.total_analyses === next.total_analyses &&
          prev.onboarding_completed === next.onboarding_completed
        ) {
          return prev
        }
        return next
      })
      setSentryUser({
        id: userId,
        email: next.email,
        plan: next.subscription_tier || 'free',
      })
    }
  }, [])

  const refreshProfile = useCallback(async () => {
    if (user) await fetchProfile(user.id)
  }, [user, fetchProfile])

  // Exposed signIn: updates state BEFORE returning, so navigate() works
  const signIn = useCallback(async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (data.user) {
      setUser(data.user)
      // Don't await — profile loads in background via onAuthStateChange
      fetchProfile(data.user.id)
    }
    return { error }
  }, [fetchProfile])

  const signOut = useCallback(async () => {
    await supabase.auth.signOut()
    setUser(null)
    setProfile(null)
    clearSentryUser()
    // Clear legacy unscoped keys + Stripe checkout residue. The
    // `betterclose:<userId>:*` namespace is intentionally preserved so
    // that the next time this user signs back in, their in-progress
    // analyses and preferences are restored. Cross-user isolation is
    // already enforced by the scoping itself — User B only ever reads
    // their own scoped keys via createUserScopedStorage(userB).
    clearCrossUserLeakResidue()
  }, [])

  useEffect(() => {
    let resolved = false

    // Safety-net timeout — Supabase Web Lock deadlock recovery.
    //
    // In rare corrupted-client scenarios (expired JWT + Web Lock contention,
    // documented in feedback_real_root_cause_auth_corruption), calls to
    // `supabase.auth.getSession()` can hang indefinitely and never resolve.
    // When that happens, `loading` stays `true` forever, which cascades into
    // every page that guards on `useAuth().loading` — in particular /crm,
    // which renders a full-page "Chargement..." spinner and locks the user
    // out of their own CRM until they hard-refresh.
    //
    // This 5-second safety timer forces `setLoading(false)` if getSession
    // hasn't resolved yet. The user then sees the app as "signed out" and
    // can retry. In the normal (non-corrupted) case, getSession resolves in
    // <100 ms and the timer is cleared before it fires — so healthy sessions
    // are completely unaffected.
    //
    // Do not remove without first fixing the root cause in Analyze.tsx's
    // 3-layer auth (see feedback_real_root_cause_auth_corruption).
    const safetyTimer = setTimeout(() => {
      if (!resolved) {
        if (import.meta.env.DEV) {
          console.warn('[auth] getSession timed out after 5s, forcing loading=false')
        }
        setLoading(false)
      }
    }, 5000)

    // Fast init from localStorage (getSession is instant, getUser makes a network call)
    supabase.auth.getSession().then(({ data: { session } }) => {
      resolved = true
      clearTimeout(safetyTimer)
      if (session?.user) {
        setUser(session.user)
        fetchProfile(session.user.id).finally(() => setLoading(false))
      } else {
        setLoading(false)
      }
    }).catch((err) => {
      resolved = true
      clearTimeout(safetyTimer)
      if (import.meta.env.DEV) console.error('[auth] getSession failed:', err)
      setLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Skip synthetic SIGNED_IN emitted by _recoverAndRefresh with an
      // unchanged access_token. See the prevAccessTokenRef block above for
      // the full rationale. Logged so future regressions are visible in prod.
      if (event === 'SIGNED_IN' && session?.access_token && session.access_token === prevAccessTokenRef.current) {
        if (import.meta.env.DEV) console.log('[auth] SIGNED_IN SKIPPED (same access_token)')
        return
      }
      if (session?.access_token) {
        prevAccessTokenRef.current = session.access_token
      }

      if (event === 'SIGNED_OUT') {
        setUser(null)
        setProfile(null)
        // Defense in depth: sign-out can also be triggered by expired
        // sessions or cross-tab logout, not only by our signOut() call.
        clearCrossUserLeakResidue()
      } else if (session?.user) {
        // Stabilize user reference when the same user re-emits (TOKEN_REFRESHED
        // fires a new session.user object even when nothing material changed).
        // Without this, every token refresh cascades a re-render into every
        // consumer of AuthContext — the documented source of the Result→Dashboard
        // render storm.
        setUser((prev) => (prev && prev.id === session.user.id ? prev : session.user))
        // Only refetch the profile on genuine identity changes. A raw
        // TOKEN_REFRESHED with the same user id doesn't need a DB round-trip.
        if (event !== 'TOKEN_REFRESHED') {
          await fetchProfile(session.user.id)
        }
      }
      setLoading(false)
    })

    return () => {
      clearTimeout(safetyTimer)
      subscription.unsubscribe()
    }
  }, [fetchProfile])

  // ─────────────────────────────────────────────────────────────
  // Profile realtime subscription.
  //
  // Why: after a Stripe payment the webhook bumps profiles.credits,
  // subscription_tier, subscription_status, subscription_credits_allocated,
  // etc. Without Realtime the frontend only sees the change the next time
  // the page hard-refreshes (or after AuthContext re-runs fetchProfile).
  // Mustapha 2026-04-20: "il faut des hard refresh entre dashboard et shop
  // c'est important" — i.e. without a live channel, the /shop UI shows the
  // old tier, the dashboard shows the old balance, and the celebration
  // feels broken.
  //
  // We scope the subscription to `id=eq.${user.id}` so a single channel
  // per auth'd user fires only for their own row. On any change we refetch
  // the full profile (cheap — ~50 ms, single row by PK) and setProfile.
  // Animated counters inside CreditsDisplay pick up the new value via
  // their existing prop path.
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user?.id) return
    const userId = user.id
    const channel = supabase
      .channel(`auth-profile-${userId}`)
      .on(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        'postgres_changes' as any,
        {
          event: '*',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${userId}`,
        },
        () => {
          void fetchProfile(userId)
        },
      )
      .subscribe()
    return () => {
      void supabase.removeChannel(channel)
    }
  }, [user?.id, fetchProfile])

  // Auto-refresh profile on tab visibility / window focus.
  // Realtime covers the connected case but a user who returns from another
  // tab after a Stripe webhook fired in the meantime needs the new credits
  // / tier / paywall state immediately. Mustapha 2026-04-21 blanket policy.
  //
  // NOTE: pathname-based refetch is NOT wired here because AuthContext is
  // rendered ABOVE BrowserRouter (see App.tsx) — useLocation() would crash.
  // Route-change profile refresh is unnecessary anyway: ProtectedRoute
  // re-evaluates on every navigation, and Realtime + visibility cover the
  // rest. Sentry BETTERCLOSE-H, fixed 2026-04-21.
  useRefreshOnFocus(refreshProfile, { enabled: !!user })

  return (
    <AuthContext.Provider value={{ user, profile, loading, signIn, signOut, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => useContext(AuthContext)
